import type { BlockyTimeDB } from '@/db/schema'
import { db as defaultDb } from '@/db/schema'
import { LOCAL_TABLES, type LocalTable } from '@/core/sync/serialize'
import { dayKeyToISO } from '@/core/time/dayKey'
import { logger } from '@/core/logging/logger'

/**
 * Export: the second backup, and the escape hatch.
 *
 * Sync already protects against a lost device. This protects against the one
 * thing sync cannot: deleting something by mistake and having that deletion
 * faithfully propagated everywhere.
 *
 * Tombstones ARE included. An export that dropped them could not reproduce
 * deletions on import, so restoring an old file would silently resurrect
 * everything you had ever deleted.
 */

export const EXPORT_FORMAT_VERSION = 1

export interface ExportBundle {
  formatVersion: number
  exportedAt: number
  appVersion: string
  tables: Partial<Record<LocalTable, Record<string, unknown>[]>>
}

export async function buildExport(db: BlockyTimeDB = defaultDb): Promise<ExportBundle> {
  const tables: ExportBundle['tables'] = {}

  for (const table of LOCAL_TABLES) {
    const rows = await db[table].toArray()
    // `_dirty` is local sync bookkeeping and means nothing in a file.
    tables[table] = rows.map((row) => {
      const copy = { ...(row as unknown as Record<string, unknown>) }
      delete copy['_dirty']
      return copy
    })
  }

  return {
    formatVersion: EXPORT_FORMAT_VERSION,
    exportedAt: Date.now(),
    appVersion: __APP_VERSION__,
    tables,
  }
}

export function exportFileName(now = new Date()): string {
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `timekeeper-${y}-${m}-${d}.json`
}

export type DeliveryMethod = 'shared' | 'downloaded' | 'cancelled'

/**
 * Hands a file to the user.
 *
 * On iOS the share sheet is the reliable route: it offers "Save to Files",
 * which is what someone actually wants, and it works from an installed
 * home-screen app where an `<a download>` can be swallowed silently. The
 * anchor stays as the fallback for desktop and anywhere sharing files is
 * unsupported.
 *
 * `navigator.share` must be reached from a user gesture, and the awaits needed
 * to read the database can spend that permission. If it is refused we fall
 * through to the anchor rather than failing.
 */
export async function deliverFile(
  text: string,
  filename: string,
  mime: string,
): Promise<DeliveryMethod> {
  const file = new File([text], filename, { type: mime })

  if (typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: filename })
      return 'shared'
    } catch (err: unknown) {
      // The user dismissing the sheet is not a failure.
      if (err instanceof Error && err.name === 'AbortError') return 'cancelled'
      logger.warn('db', 'Share failed; falling back to download', err)
    }
  }

  const url = URL.createObjectURL(new Blob([text], { type: mime }))
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.rel = 'noopener'
  document.body.appendChild(link)
  link.click()
  link.remove()

  // Revoke on the next tick; revoking synchronously can cancel the download
  // before it has started.
  setTimeout(() => URL.revokeObjectURL(url), 2000)
  return 'downloaded'
}

export interface ExportOutcome {
  rows: number
  method: DeliveryMethod
}

export async function downloadExport(db: BlockyTimeDB = defaultDb): Promise<ExportOutcome> {
  const bundle = await buildExport(db)
  const json = JSON.stringify(bundle, null, 2)

  const method = await deliverFile(json, exportFileName(), 'application/json')

  logger.info('db', `Exported ${countRows(bundle)} rows (${method})`)
  return { rows: countRows(bundle), method }
}

export function countRows(bundle: ExportBundle): number {
  return Object.values(bundle.tables).reduce((n, rows) => n + (rows?.length ?? 0), 0)
}

/** Live (non-tombstoned) entry count, for the UI summary. */
export function countLiveEntries(bundle: ExportBundle): number {
  return (bundle.tables.entries ?? []).filter((r) => r['deletedAt'] === null).length
}

// -----------------------------------------------------------------------------
// CSV -- a human-readable view, not a backup. Import reads JSON only.
// -----------------------------------------------------------------------------

export interface CsvNameLookup {
  categoryOf: (eventTypeId: string | null) => string
  typeName: (eventTypeId: string | null) => string
  objectName: (eventObjectId: string | null) => string
}

function csvCell(value: string): string {
  return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value
}

export function buildCsv(
  bundle: ExportBundle,
  names: CsvNameLookup,
  formatTime: (ms: number) => string,
): string {
  const header =
    'date,start,end,duration_minutes,category,event_type,event_object,comment,planned'

  const rows = (bundle.tables.entries ?? [])
    .filter((r) => r['deletedAt'] === null)
    .sort((a, b) => Number(a['startAt']) - Number(b['startAt']))
    .map((r) => {
      const startAt = Number(r['startAt'])
      const duration = Number(r['durationMinutes'])
      const typeId = (r['eventTypeId'] as string | null) ?? null
      const objectId = (r['eventObjectId'] as string | null) ?? null

      return [
        dayKeyToISO(Number(r['dayKey'])),
        formatTime(startAt),
        formatTime(startAt + duration * 60_000),
        String(duration),
        csvCell(names.categoryOf(typeId)),
        csvCell(names.typeName(typeId)),
        csvCell(names.objectName(objectId)),
        csvCell((r['comment'] as string | null) ?? ''),
        r['isPlanned'] === 1 ? 'true' : 'false',
      ].join(',')
    })

  return [header, ...rows].join('\n')
}

export function downloadText(text: string, filename: string, mime: string): void {
  const blob = new Blob([text], { type: mime })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
